import React, { useEffect, useState } from 'react';
import { Form, Radio, Select, Input, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { RevxButton } from 'components/RevxButton';
import shippingService from 'services/shippingService';
import { useMount } from 'ahooks';
import { useOrgId } from 'hooks/useOrg';
import { RevxSpin } from 'components/RevxSpin';
import { OrgDistribution } from 'domains/shippingDomain';

export type selectOptionsType = {
  label: string;
  value: string;
  key: string;
};

const VALUE_FLAG = '@__@';
const valueFlagRegex: RegExp = new RegExp(`^${VALUE_FLAG}.*${VALUE_FLAG}$`);
const generateValueFlagString = (v: string) => {
  return `${VALUE_FLAG}${v}${VALUE_FLAG}`;
};
const formatOptions = (v: OrgDistribution[]): selectOptionsType[] => {
  return v.map((i) => {
    i.distributions.push(generateValueFlagString(i.orgName));
    const distribution = JSON.stringify(i.distributions);
    return {
      value: distribution,
      label: i.orgName,
      key: i.orgName + distribution,
    };
  });
};

const InternalDistribution = () => {
  const [locationListForm] = Form.useForm();
  const [distributions, setDistributions] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<selectOptionsType[]>([]);
  const orgId = useOrgId();

  useMount(async () => {
    setLoading(true);
    const { distributions, display, orgDistributions } =
      await shippingService.getInternalDistribution(orgId);
    setDistributions(distributions);
    setOptions(formatOptions(orgDistributions));
    locationListForm.setFieldsValue({ display });
    setVisible(display);
    setLoading(false);
  });

  const onFinish = (text: string) => {
    const newData = [
      ...distributions.slice(0, editingIndex!),
      text,
      ...distributions.slice(editingIndex! + 1),
    ];
    setDistributions(newData);
    setEditingIndex(null);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleDelete = (index: number) => {
    setDistributions(distributions.filter((_, i) => i !== index));
  };

  const handleBlur = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const v = event.target.value.trim();
    if (!v) {
      handleDelete(index);
    } else {
      onFinish(v);
    }
  };

  const handleAdd = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const currentIndex = distributions.length;
    setEditingIndex(currentIndex);
    setDistributions([...distributions, '']);
  };

  const updateDistribution = async () => {
    const { display } = locationListForm.getFieldsValue();
    setLoading(true);
    await shippingService.updateInternalDistribution(orgId, {
      display,
      distributions,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!distributions.filter((i) => !i).length) {
      updateDistribution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributions]);

  return (
    <div className="internal-distribution">
      <RevxSpin spinning={loading}>
        <Form
          form={locationListForm}
          onValuesChange={(v) => {
            if (v.orgDistributions) {
              const parseOrgDistributions = JSON.parse(v.orgDistributions);
              const filterOrgDistributions = parseOrgDistributions.filter(
                (i: string) => !valueFlagRegex.exec(i)
              );
              setDistributions(filterOrgDistributions);
            }
          }}
        >
          <Form.Item name="display" label="Display for this organization:">
            <Radio.Group
              defaultValue={false}
              onChange={async (e) => {
                setVisible(e.target.value);
                await updateDistribution();
              }}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>
          {visible && (
            <div className="hide-block">
              <Form.Item name="orgDistributions" label="Copy and edit from: ">
                <Select
                  allowClear={true}
                  options={options}
                  placeholder="Select Organization"
                />
              </Form.Item>
              <div className="mb-20">
                <p className="sub-title">
                  List distribution here (one per line):
                </p>
                <RevxButton
                  onClick={handleAdd}
                  icon={<PlusOutlined />}
                  type="primary"
                  brandGreen
                  className="mb-10"
                >
                  ADD
                </RevxButton>
                <div className="location-list">
                  <Form.List name="location">
                    {(fields) => (
                      <>
                        <List
                          className="mb-20"
                          dataSource={distributions}
                          renderItem={(item: string, index: number) => {
                            const isValueFlagString =
                              !valueFlagRegex.exec(item);
                            return (
                              <List.Item
                                hidden={!isValueFlagString}
                                actions={[
                                  <EditOutlined
                                    key="edit"
                                    onClick={() => handleEdit(index)}
                                  />,
                                  <DeleteOutlined
                                    key="delete"
                                    onClick={() => handleDelete(index)}
                                  />,
                                ]}
                              >
                                <div>
                                  {editingIndex === index ? (
                                    <Form.Item
                                      name={index.toString()}
                                      initialValue={item}
                                    >
                                      <Input
                                        style={{ width: 380 }}
                                        onBlur={(event) =>
                                          handleBlur(event, index)
                                        }
                                        autoFocus
                                      />
                                    </Form.Item>
                                  ) : (
                                    <div
                                      style={{
                                        width: 380,
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {item}
                                    </div>
                                  )}
                                </div>
                              </List.Item>
                            );
                          }}
                        />
                      </>
                    )}
                  </Form.List>
                </div>
              </div>
            </div>
          )}
        </Form>
      </RevxSpin>
    </div>
  );
};

export default InternalDistribution;
